import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Box } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { useDispatch, useSelector } from 'react-redux';

import { fetchCustomers } from '../api/customers';
import { setCustomer, selectCustomer } from '../store/cartSlice';

/**
 * Async customer search using MUI Autocomplete.
 * Dispatches setCustomer to Redux on selection.
 */
export default function CustomerSelect({ defaultCustomer }) {
  const dispatch = useDispatch();
  const currentCustomer = useSelector(selectCustomer);

  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  // Set default customer from POS Profile on mount
  useEffect(() => {
    if (defaultCustomer && !currentCustomer) {
      dispatch(setCustomer({ name: defaultCustomer, customer_name: defaultCustomer }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCustomer]);

  // Debounced customer search
  useEffect(() => {
    if (!inputValue || inputValue.length < 2) {
      setOptions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await fetchCustomers(inputValue);
        setOptions(results);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue]);

  return (
    <Box sx={{ p: 1.5, pb: 0 }}>
      <Autocomplete
        size="small"
        options={options}
        loading={loading}
        getOptionLabel={(opt) =>
          typeof opt === 'string' ? opt : opt.customer_name || opt.name || ''
        }
        isOptionEqualToValue={(opt, val) => opt.name === val.name}
        value={currentCustomer}
        onChange={(_, newValue) => {
          dispatch(setCustomer(newValue));
        }}
        inputValue={inputValue}
        onInputChange={(_, newInput) => setInputValue(newInput)}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search customer..."
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <PersonIcon color="action" sx={{ mr: 0.5, fontSize: 20 }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={inputValue.length < 2 ? 'Type to search...' : 'No customers found'}
        clearOnBlur={false}
      />
    </Box>
  );
}
